import { useMemo, useState } from 'react';
import { newsFeed, type FeedItem } from '@/mocks/newsFeed';

const CATS: Array<'全部' | FeedItem['category']> = ['全部', '综艺', '影视', '明星', '行业'];
const CAT_COLOR: Record<string, string> = {
  综艺: 'bg-primary-100 text-primary-700',
  影视: 'bg-accent-100 text-accent-800',
  明星: 'bg-secondary-100 text-secondary-800',
  行业: 'bg-background-200 text-foreground-700',
};

function hotNum(h: string) {
  const n = parseFloat(h.replace('万', ''));
  return Number.isFinite(n) ? n : 0;
}

export default function Feed() {
  const [cat, setCat] = useState<(typeof CATS)[number]>('全部');
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState<'time' | 'hot'>('time');

  const list = useMemo(() => {
    let arr = newsFeed.filter((f) => (cat === '全部' ? true : f.category === cat));
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      arr = arr.filter(
        (f) => f.title.toLowerCase().includes(q) || f.summary.toLowerCase().includes(q),
      );
    }
    if (sortBy === 'hot') arr = [...arr].sort((a, b) => hotNum(b.hot) - hotNum(a.hot));
    return arr;
  }, [cat, query, sortBy]);

  return (
    <section id="feed" className="scroll-mt-16 bg-background-100">
      <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-background-300 pb-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-news-line text-lg" />
            </span>
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">全量吃瓜流</h2>
              <p className="text-[11px] tracking-[0.25em] text-foreground-500 md:text-xs">LIVE FEED · 每日滚动更新</p>
            </div>
          </div>
          <p className="text-xs text-foreground-500">
            当前共 <span className="font-semibold text-primary-600">{list.length}</span> 条内容
          </p>
        </div>

        {/* 工具栏 */}
        <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {CATS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition ${
                  cat === c
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-50 text-foreground-700 ring-1 ring-background-300 hover:bg-background-200'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 md:w-72">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜瓜：综艺 / 影视 / 关键词…"
                className="w-full rounded-full border border-background-300 bg-background-50 py-2 pl-9 pr-10 text-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                aria-label="站内搜索"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-500 hover:text-background-50"
                  aria-label="清空搜索"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 排序条 */}
        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs text-foreground-500">{query ? `“${query}” 的搜索结果` : '按发布时间排序'}</span>
          <div className="flex rounded-full bg-background-50 p-1 ring-1 ring-background-300">
            <button
              type="button"
              onClick={() => setSortBy('time')}
              className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition ${
                sortBy === 'time' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-primary-600'
              }`}
            >
              <i className="ri-time-line" /> 最新
            </button>
            <button
              type="button"
              onClick={() => setSortBy('hot')}
              className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition ${
                sortBy === 'hot' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-primary-600'
              }`}
            >
              <i className="ri-fire-fill" /> 最热
            </button>
          </div>
        </div>

        {/* 列表 */}
        {list.length === 0 ? (
          <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-inbox-2-line text-4xl text-foreground-300" />
            <p className="mt-3 font-medium text-foreground-700">暂时没搜到相关的大瓜</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或清空筛选回到全部内容</p>
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setCat('全部');
              }}
              className="mt-4 flex items-center gap-1 whitespace-nowrap rounded-full bg-foreground-950 px-4 py-2 text-sm text-background-50 transition hover:bg-primary-600"
            >
              <i className="ri-refresh-line" /> 重置筛选
            </button>
          </div>
        ) : (
          <ul className="mt-5 divide-y divide-background-200 overflow-hidden rounded-2xl border border-background-200 bg-background-50">
            {list.map((f) => (
              <li key={f.id} className="flex gap-4 px-4 py-4 transition hover:bg-background-100/70 md:px-5">
                <span className="mt-0.5 hidden text-xs font-heading font-bold text-background-400 sm:block">
                  {String(newsFeed.findIndex((x) => x.id === f.id) + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${CAT_COLOR[f.category]}`}>
                      {f.category}
                    </span>
                    <span className="text-xs text-foreground-400">{f.timeLabel}</span>
                    <span className="text-xs text-foreground-400">来自 {f.source}</span>
                  </div>
                  <h3 className="mt-1.5 font-heading text-[15px] font-bold leading-snug text-foreground-900 md:text-base">
                    {f.title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">{f.summary}</p>
                </div>
                <div className="hidden shrink-0 flex-col items-end justify-center gap-2 md:flex">
                  {f.hot !== '—' && (
                    <span className="flex items-center gap-1 text-sm font-semibold text-primary-600">
                      <i className="ri-fire-fill text-accent-500" /> {f.hot}
                    </span>
                  )}
                  {f.comment > 0 && (
                    <span className="flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-chat-3-line" /> {f.comment.toLocaleString()} 条讨论
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}